import { useRef } from 'react';
import { quoteMerchandise, type MerchandiseQuote, type StorefrontProduct } from '../api/client';
import { useStoreRead } from '../elements/render/useStoreRead';
import type { Commerce } from './ShopCommerce';

// A cash-on-delivery page sells one product: a shopper who lands on it from an ad has an empty cart, and the
// order form beside the product orders that product, in the option and quantity picked above it — as Buy it now
// orders one product without the cart. This is that choice as a one-line cart the form can place, priced by the
// server like any cart, whose order leaves the shopper's own cart as it was. While a new
// quantity is being priced the last price stays, so the form never blinks out from under the shopper.

/** The product, variant and quantity chosen on the page. */
export type PageLine = { product: StorefrontProduct; variantId: string; quantity: number };

const PRICING_FAILED = 'This product could not be priced right now. Please try again.';

// Prices the page's own choice as a one-line cart, or answers null when there is no choice to order.
export function usePageLineCart(base: Commerce | null, line: PageLine | null): Commerce | null {
  const key = line ? `page-line:${line.product.id}:${line.variantId}:${line.quantity}` : null;
  const read = useStoreRead<MerchandiseQuote>(key, () => quoteMerchandise([{ productId: line!.product.id, variantId: line!.variantId, quantity: line!.quantity }]));
  const last = useRef<MerchandiseQuote | null>(null);
  if (read.data) last.current = read.data;
  if (!base || !line) return null;
  const lines = [{ slug: line.product.slug, variantId: line.variantId, quantity: line.quantity }];
  const quote = read.data ?? last.current;
  return {
    ...base,
    lines,
    products: [line.product],
    quote,
    loading: read.status === 'loading',
    error: read.status === 'failed' || read.status === 'missing' ? PRICING_FAILED : '',
    // The order holds this one line: its confirmation lists it, and the cart stays as the shopper left it.
    complete: (raw) => base.completeLine(raw, { lines, products: [line.product], quote })
  };
}
